/**
 * Book Detail — the activity feed for one book.
 *
 * This is the SAME table the couple's own dashboard reads, so what an operator
 * sees here is exactly what the couple sees. That is deliberate: an admin
 * action that appears in this list appears in theirs too, and an admin action
 * whose reason is NOT here (suspension notes go to admin_audit_log) is not
 * hidden from us by accident — it is hidden from them on purpose.
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Tooltip,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import MicIcon from '@mui/icons-material/Mic';
import VideocamIcon from '@mui/icons-material/Videocam';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EventIcon from '@mui/icons-material/Event';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { activityLabel, relativeTime, dateTimeInZone } from '../weddingFormat';
import type { ActivityEvent } from '../weddingApi';

type PaletteKey = 'info' | 'success' | 'warning' | 'error' | 'primary';

const EVENT_ICON: Record<string, { icon: React.ReactNode; color: PaletteKey }> = {
  guest_invited: { icon: <MailOutlineIcon fontSize="small" />, color: 'info' },
  guest_verified: { icon: <HowToRegIcon fontSize="small" />, color: 'info' },
  chapter_started: { icon: <EditNoteIcon fontSize="small" />, color: 'warning' },
  chapter_submitted: { icon: <TaskAltIcon fontSize="small" />, color: 'success' },
  photos_added: { icon: <PhotoLibraryIcon fontSize="small" />, color: 'warning' },
  voice_added: { icon: <MicIcon fontSize="small" />, color: 'warning' },
  video_added: { icon: <VideocamIcon fontSize="small" />, color: 'warning' },
  reminder_sent: { icon: <NotificationsActiveIcon fontSize="small" />, color: 'info' },
  book_activated: { icon: <AutoStoriesIcon fontSize="small" />, color: 'success' },
  deadline_extended: { icon: <EventIcon fontSize="small" />, color: 'primary' },
  book_suspended: { icon: <PauseCircleIcon fontSize="small" />, color: 'error' },
  book_reactivated: { icon: <PlayCircleIcon fontSize="small" />, color: 'success' },
};

interface Props {
  events: ActivityEvent[];
  timezone?: string;
}

const ActivityTimeline: React.FC<Props> = ({ events, timezone }) => (
  <Card sx={{ borderRadius: '16px' }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Activity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The same feed the couple sees on their own dashboard.
      </Typography>

      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing has happened on this book yet.
        </Typography>
      ) : (
        <List dense sx={{ maxHeight: 460, overflow: 'auto' }}>
          {events.map((event) => {
            const meta = EVENT_ICON[event.event_type] || {
              icon: <HelpOutlineIcon fontSize="small" />,
              color: 'primary' as PaletteKey,
            };
            return (
              <ListItem key={event.id} disableGutters>
                <ListItemAvatar>
                  <Avatar
                    sx={{ width: 32, height: 32, bgcolor: `${meta.color}.main` }}
                    aria-hidden
                  >
                    {meta.icon}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={activityLabel(event.event_type, event.actor_name)}
                  secondary={
                    <Tooltip title={dateTimeInZone(event.created_at, timezone)}>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {relativeTime(event.created_at)}
                      </Typography>
                    </Tooltip>
                  }
                  slotProps={{ primary: { variant: 'body2' } }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </CardContent>
  </Card>
);

export default ActivityTimeline;
